/**
 * Story 成片可见范围与分享（本人），短剧频道与按链接观看（游客可用）。
 * 可见范围三档：private 仅本人、link 有链接可看、public 公开到短剧频道。
 */

const VISIBILITIES = ['private', 'link', 'public']

/**
 * @param {{ request: Function }} transport
 */
export function createStoryFilmPublicApi(transport) {
  if (!transport || typeof transport.request !== 'function') throw new Error('createStoryFilmPublicApi: transport.request is required')
  return {
    /** 本人非仅本人可见的集及其可见范围。 */
    listMyVisibility() {
      return transport.request('GET', '/api/visual-game/me/story-film/visibility', {
        parse: value => {
          if (!isObject(value) || !Array.isArray(value.episodes)) throw new Error('Story film visibility list is invalid')
          return value.episodes.map(item => ({ episode_id: requireId(item.episode_id), visibility: requireVisibility(item.visibility) }))
        },
      })
    },
    /**
     * 设置本集可见范围。
     * @param {{ episode_id: string, visibility: 'private'|'link'|'public' }} body
     */
    setVisibility({ episode_id, visibility }) {
      return transport.request('POST', `/api/visual-game/me/story-film/episodes/${encodeURIComponent(requireId(episode_id))}/visibility`, {
        body: { visibility: requireVisibility(visibility) },
        parse: value => ({ episode_id: requireId(value?.episode_id), visibility: requireVisibility(value?.visibility) }),
      })
    },
    /** 分享本集：仅本人的变为有链接可看；返回观看页路径。 @param {string} episodeId */
    share(episodeId) {
      return transport.request('POST', `/api/visual-game/me/story-film/episodes/${encodeURIComponent(requireId(episodeId))}/share`, {
        body: {},
        parse: value => {
          if (!isObject(value) || typeof value.path !== 'string' || !value.path.startsWith('/dramas/watch?')) throw new Error('Story film share response is invalid')
          return { episode_id: requireId(value.episode_id), visibility: requireVisibility(value.visibility), path: value.path }
        },
      })
    },
    /** 短剧频道：公开到频道的玩家短剧。 */
    listDramas() {
      return transport.request('GET', '/api/visual-game/story-film/public/dramas', {
        parse: value => {
          if (!isObject(value) || !Array.isArray(value.dramas)) throw new Error('Story film public dramas response is invalid')
          return value.dramas.map(parseDramaCard)
        },
      })
    },
    /** 频道里一部短剧的第一集公开集。 @param {string} filmId */
    firstEpisode(filmId) {
      return transport.request('GET', `/api/visual-game/story-film/public/dramas/${encodeURIComponent(requireId(filmId))}/first-episode`, {
        parse: value => requireId(value?.episode_id),
      })
    },
    /** 本集各渠道最近一次社媒分发记录。 @param {string} episodeId */
    listPublications(episodeId) {
      return transport.request('GET', `/api/visual-game/me/story-film/episodes/${encodeURIComponent(requireId(episodeId))}/publications`, {
        parse: value => {
          if (!isObject(value) || !Array.isArray(value.publications)) throw new Error('Story film publications response is invalid')
          return value.publications.map(item => ({ request_id: requireId(item.request_id), channel_id: requireId(item.channel_id), platform: String(item.platform), created_at: String(item.created_at) }))
        },
      })
    },
    /**
     * 提交到一个已绑定渠道后记下一次社媒分发。
     * @param {{ episode_id: string, request_id: string, channel_id: string, platform: string }} body
     */
    recordPublication({ episode_id, request_id, channel_id, platform }) {
      return transport.request('POST', `/api/visual-game/me/story-film/episodes/${encodeURIComponent(requireId(episode_id))}/publications`, {
        body: { request_id: requireId(request_id), channel_id: requireId(channel_id), platform: String(platform) },
        parse: value => ({ request_id: requireId(value?.request_id) }),
      })
    },
    /** 一集的播放、点赞、收藏数与自己是否点过；游客也可读。 @param {string} episodeId */
    getEngagement(episodeId) {
      return transport.request('GET', episodePath(episodeId, 'engagement'), { parse: parseEngagement })
    },
    /** 记一次播放。 @param {string} episodeId */
    recordPlay(episodeId) {
      return transport.request('POST', episodePath(episodeId, 'play'), { body: {}, parse: parseEngagement })
    },
    /** 点赞或取消点赞。 @param {string} episodeId */
    toggleLike(episodeId) {
      return transport.request('POST', episodePath(episodeId, 'like'), { body: {}, parse: parseEngagement })
    },
    /** 收藏或取消收藏。 @param {string} episodeId */
    toggleFavorite(episodeId) {
      return transport.request('POST', episodePath(episodeId, 'favorite'), { body: {}, parse: parseEngagement })
    },
    /** 我收藏的 Story 集，最近收藏的在前。 */
    listMyFavorites() {
      return transport.request('GET', '/api/visual-game/me/story-film/favorites', {
        parse: value => {
          if (!isObject(value) || !Array.isArray(value.episodes)) throw new Error('Story film favorites response is invalid')
          return value.episodes.map(parseFavorite)
        },
      })
    },
    /** 按链接看一集：本集信息、播放地址与同剧可切换的集。 @param {string} episodeId */
    getSharedEpisode(episodeId) {
      return transport.request('GET', `/api/visual-game/story-film/public/episodes/${encodeURIComponent(requireId(episodeId))}`, {
        parse: parseSharedEpisode,
      })
    },
  }
}

function parseDramaCard(value) {
  if (!isObject(value)) throw new Error('Story film public drama is invalid')
  return {
    film_id: requireId(value.film_id),
    first_episode_id: typeof value.first_episode_id === 'string' ? value.first_episode_id : null,
    cover_url: typeof value.cover_url === 'string' ? value.cover_url : null,
    summary: typeof value.summary === 'string' && value.summary !== '' ? value.summary : null,
    play_count: Number(value.play_count) || 0,
    like_count: Number(value.like_count) || 0,
    favorite_count: Number(value.favorite_count) || 0,
    title: String(value.title ?? ''),
    source_title: String(value.source_title ?? ''),
    source_slug: String(value.source_slug ?? ''),
    player_name: value.player_name === null ? null : String(value.player_name),
    episode_count: Number(value.episode_count),
    latest_season_no: Number(value.latest_season_no),
    updated_at: String(value.updated_at),
  }
}

function parseSharedEpisode(value) {
  if (!isObject(value) || !isObject(value.episode) || !Array.isArray(value.episodes) || typeof value.playback_url !== 'string') throw new Error('Story film shared episode is invalid')
  return {
    film_id: requireId(value.film_id),
    title: String(value.title ?? ''),
    source_title: String(value.source_title ?? ''),
    source_slug: String(value.source_slug ?? ''),
    player_name: value.player_name === null ? null : String(value.player_name),
    episode: {
      episode_id: requireId(value.episode.episode_id),
      season_no: Number(value.episode.season_no),
      episode_no: Number(value.episode.episode_no),
      title: value.episode.title === null ? null : String(value.episode.title),
      summary: value.episode.summary === null ? null : String(value.episode.summary),
      duration_ms: Number(value.episode.duration_ms),
    },
    playback_url: value.playback_url,
    episodes: value.episodes.map(item => ({ episode_id: requireId(item.episode_id), season_no: Number(item.season_no), episode_no: Number(item.episode_no) })),
  }
}

function episodePath(episodeId, action) {
  return `/api/visual-game/story-film/public/episodes/${encodeURIComponent(requireId(episodeId))}/${action}`
}

function parseEngagement(value) {
  if (!isObject(value)) throw new Error('Story film engagement is invalid')
  return {
    episode_id: requireId(value.episode_id),
    play_count: Number(value.play_count),
    like_count: Number(value.like_count),
    favorite_count: Number(value.favorite_count),
    is_liked: value.is_liked === true,
    is_favorited: value.is_favorited === true,
  }
}

function parseFavorite(value) {
  if (!isObject(value)) throw new Error('Story film favorite is invalid')
  return {
    episode_id: requireId(value.episode_id),
    film_id: requireId(value.film_id),
    title: String(value.title ?? ''),
    source_title: String(value.source_title ?? ''),
    source_slug: String(value.source_slug ?? ''),
    player_name: value.player_name === null ? null : String(value.player_name),
    season_no: Number(value.season_no),
    episode_no: Number(value.episode_no),
    cover_url: String(value.cover_url),
    path: String(value.path),
    favorited_at: String(value.favorited_at),
    play_count: Number(value.play_count) || 0,
    like_count: Number(value.like_count) || 0,
  }
}

function requireVisibility(value) {
  if (!VISIBILITIES.includes(value)) throw new Error('Story film visibility is invalid')
  return value
}

function requireId(value) {
  if (typeof value !== 'string' || value.trim() === '') throw new Error('Story film id is invalid')
  return value
}

function isObject(value) {
  return value !== null && typeof value === 'object'
}
